// An SVG onto the paper (Robin, 2026-10-23; no button yet: drop a file on the paper, or __pumpkin.importSvg). The file
// is only ever drawn as an image, never put into the page: an SVG in an <img> runs no script and loads nothing else,
// so whatever it holds ends up as pixels. Its dark, opaque parts are ink; the ink is traced back into solid shapes
// (`traceField`, as Move does) and fitted into the face's area of the sheet.

import { traceField, type Region } from './regions';

const MAX_BYTES = 2 << 20;
/** the face's area: presets sit inside about x +-0.6, y +-0.7 */
const HALF_W = 0.62, HALF_H = 0.66;

/** the file's own size, from width and height or the viewBox; null if it isn't an SVG */
function size(doc: Document): [number, number] | null {
  const root = doc.documentElement;
  if (root.nodeName !== 'svg' || doc.getElementsByTagName('parsererror').length) return null;
  const vb = (root.getAttribute('viewBox') ?? '').split(/[\s,]+/).map(Number);
  const len = (a: string) => { const v = root.getAttribute(a) ?? ''; return /%/.test(v) ? NaN : parseFloat(v); };
  let w = len('width'), h = len('height');
  if (vb.length === 4 && vb.every(Number.isFinite) && vb[2] > 0 && vb[3] > 0) {
    if (!(w > 0) && !(h > 0)) { w = vb[2]; h = vb[3]; } else if (!(w > 0)) w = (h * vb[2]) / vb[3]; else if (!(h > 0)) h = (w * vb[3]) / vb[2];
  }
  return w > 0 && h > 0 ? [w, h] : null;
}

/** the SVG's ink as solid shapes in sheet coordinates, fitted into the face's area */
export async function svgRegions(text: string, n = 768): Promise<Region[]> {
  if (text.length > MAX_BYTES) throw new Error('That SVG is too big');
  const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
  const wh = size(doc);
  if (!wh) throw new Error("That isn't an SVG we can read");
  // fit it, keeping its shape, then tell the file that size so it draws there and not at 300x150
  const k = Math.min((HALF_W * n) / wh[0], (HALF_H * n) / wh[1]);
  const w = Math.max(1, Math.round(wh[0] * k)), h = Math.max(1, Math.round(wh[1] * k));
  const root = doc.documentElement;
  if (!root.getAttribute('viewBox')) root.setAttribute('viewBox', `0 0 ${wh[0]} ${wh[1]}`);
  root.setAttribute('width', String(w));
  root.setAttribute('height', String(h));
  root.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(doc)], { type: 'image/svg+xml' }));
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    const cv = document.createElement('canvas');
    cv.width = cv.height = n;
    const g = cv.getContext('2d', { willReadFrequently: true })!;
    g.drawImage(img, (n - w) / 2, (n - h) / 2, w, h);
    // ink is what is both opaque and dark: black lines on white or on nothing alike
    const px = g.getImageData(0, 0, n, n).data, f = new Float32Array(n * n);
    for (let i = 0; i < f.length; i++) {
      const lum = (0.2126 * px[i * 4] + 0.7152 * px[i * 4 + 1] + 0.0722 * px[i * 4 + 2]) / 255;
      f[i] = (px[i * 4 + 3] / 255) * (1 - lum);
    }
    return traceField(f, n);
  } finally {
    URL.revokeObjectURL(url);
  }
}
